import { Link } from "react-router-dom";
import { Crosshair, ArrowLeft } from "lucide-react";

function LegalShell({ title, updated, children }) {
  return (
    <div className="relative z-10 min-h-screen bg-background">
      <header className="sticky top-0 z-40 bg-[#0d0d0d]/90 backdrop-blur border-b border-[#27272a]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Crosshair className="text-volt" size={22} strokeWidth={2.5} />
            <span className="font-heading font-black text-lg uppercase tracking-tight">Spar<span className="text-volt">Base</span></span>
          </Link>
          <Link to="/auth" className="text-sm uppercase tracking-wider text-zinc-400 hover:text-volt transition-colors duration-200 flex items-center gap-1"><ArrowLeft size={14} /> Back</Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <h1 className="font-heading font-extrabold uppercase text-4xl sm:text-5xl tracking-tight">{title}</h1>
        <p className="text-xs text-zinc-500 uppercase tracking-widest mt-2">Last updated {updated}</p>
        <div className="mt-8 space-y-6 text-zinc-300 leading-relaxed text-sm [&_h2]:font-heading [&_h2]:text-xl [&_h2]:uppercase [&_h2]:tracking-tight [&_h2]:text-white [&_h2]:mt-8 [&_h2]:mb-2 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1">
          {children}
        </div>
        <p className="text-xs text-zinc-600 mt-12 border-t border-[#27272a] pt-6">
          SparBase is a digital networking and coordination tool only. SparBase takes zero liability for injuries or disputes. All users spar entirely at their own risk.
        </p>
      </main>
    </div>
  );
}

export function TermsPage() {
  return (
    <LegalShell title="Terms of Service & Disclaimer" updated="June 2026">
      <h2>1. What SparBase is</h2>
      <p>SparBase is a software tool that helps combat-sports athletes and coaches discover training partners and coordinate sparring sessions and training camps. SparBase is a digital directory and coordination platform only. We do not organise, supervise, referee, insure, or take part in any physical training or sparring.</p>

      <h2>2. No liability for physical activity</h2>
      <p>Sparring and combat-sports training carry an inherent risk of serious injury. By using SparBase you acknowledge and agree that:</p>
      <ul>
        <li>SparBase is <strong>not responsible or liable</strong> for any injury, loss, or damage arising from any training, sparring, or contact arranged through the platform.</li>
        <li>SparBase is not liable for matching outcomes, no-shows, conduct, or disputes between users or gyms.</li>
        <li>You participate in all physical activity entirely at your own risk and are responsible for assessing the suitability and safety of any partner or session.</li>
      </ul>

      <h2>3. Your responsibilities</h2>
      <ul>
        <li>You must provide accurate information about yourself, your gym, and your experience.</li>
        <li>You are responsible for ensuring appropriate supervision, medical clearance, insurance, and protective equipment for any session.</li>
        <li>You must comply with the rules of your gym and any relevant governing body.</li>
      </ul>

      <h2>4. Trainer & gym accounts</h2>
      <p>Trainer accounts confirm at registration that they are an authorised coach or gym representative responsible for the safety and supervision of the fighters in their stable. Misrepresentation of credentials is a breach of these terms and shifts all resulting liability to the account holder.</p>

      <h2>5. Subscriptions</h2>
      <p>Certain features (such as trainer and gym profiles) may be offered on a paid subscription. Pricing, billing terms and any free-trial period will be shown clearly before you subscribe.</p>

      <h2>6. Termination</h2>
      <p>We may suspend or remove accounts that breach these terms, misrepresent credentials, or put other users at risk.</p>

      <h2>7. Governing law</h2>
      <p>These terms are governed by the laws of England and Wales.</p>
    </LegalShell>
  );
}

export function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy" updated="June 2026">
      <p>This policy explains how SparBase collects and uses personal data in line with the UK GDPR and the Data Protection Act 2018.</p>

      <h2>1. Who we are</h2>
      <p>SparBase is the data controller for the personal data processed through this platform. For any data request, contact us at privacy@sparbase.uk.</p>

      <h2>2. What we collect</h2>
      <ul>
        <li><strong>Fighters:</strong> name, gym, city/region, discipline, stance, weight, height, experience, photo, and account credentials.</li>
        <li><strong>Trainers:</strong> the above plus full legal name, business/gym name, phone number, governing-body affiliation and any coaching licence details you provide.</li>
        <li><strong>Activity data:</strong> stable membership, camps, sparring requests, scheduled sessions and logged sessions.</li>
      </ul>

      <h2>3. Why we use it (lawful basis)</h2>
      <ul>
        <li>To operate the matchmaking directory and coordinate sessions (performance of contract).</li>
        <li>To keep the platform safe and verify coaching claims (legitimate interests).</li>
        <li>To process subscriptions where applicable (performance of contract).</li>
      </ul>

      <h2>4. Sharing</h2>
      <p>Your profile details are visible to other SparBase users to enable matching. Trainers can see the fighters in their stable and the partners added to a camp. We do not sell your personal data. We use trusted processors (e.g. hosting, email and storage providers) under appropriate agreements.</p>

      <h2>5. Your rights</h2>
      <p>Under UK GDPR you have the right to access, correct, delete, restrict or port your data, and to object to certain processing. You can exercise these rights at any time by contacting privacy@sparbase.uk. You may also complain to the Information Commissioner's Office (ICO).</p>

      <h2>6. Retention & security</h2>
      <p>We keep your data for as long as your account is active and only as long as necessary afterwards. Passwords are stored hashed, and access to data is restricted.</p>
    </LegalShell>
  );
}
